import React from 'react';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import AppBar from '@material-ui/core/AppBar';
import Toolbar from '@material-ui/core/Toolbar';
import LanguageIcon from '@material-ui/icons/Translate';
import Button from '@material-ui/core/Button';
import { MenuItem, Menu, useTheme, Grid, useMediaQuery, Typography, Link, List, ListItem, ListItemText, IconButton, Box } from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { Paper, Container } from '@material-ui/core';
import logo from '../assets/img/logo.png'
import revolugroupLogo from '../assets/img/revolugroup-darkbackground-logo.png';
import playstoreLogo from '../assets/img/playstore.svg'
import appStoreLogo from '../assets/img/appstore.svg'
import { useTranslation, Trans } from 'react-i18next';
import FacebookIcon from '@material-ui/icons/Facebook';
import LinkedInIcon from '@material-ui/icons/LinkedIn';
import { useCookies } from 'react-cookie';
import { Alert } from '@material-ui/lab';
import { Link as RouterLink } from 'react-router-dom';


const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      flexGrow: 1,
      display: 'flex',
      flexDirection: 'column'
    },
    menuButton: {
      marginRight: theme.spacing(2),
    },
    logoContainer: {
      flexGrow: 1,
    },
    logo: {
      maxHeight: 80,
      maxWidth: '100%'
    },
    container: {
      flexGrow: 1,
      paddingTop: theme.spacing(2),
      paddingBottom: theme.spacing(6)
    },
    footerContainer: {
      backgroundColor: theme.palette.primary.main,
    },
    footerWrapper: {
      paddingTop: theme.spacing(2),
      paddingBottom: theme.spacing(2)
    },
    footerText: {
      color: theme.palette.primary.contrastText
    },
    toolBarContainer: {
      paddingLeft: 0,
      paddingRight: 0
    }
  }),
);


const LanguageButton = () => {
  const { t, i18n } = useTranslation('layout');
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  const openLanguageMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const closeLanguageMenu = () => {
    setAnchorEl(null);
  };

  const setLanguage = (language: 'en' | 'es') => {
    i18n.changeLanguage(language);
    closeLanguageMenu();
  }

  const getCurrentLanguage = () => {
    if (i18n.language.includes('es')) {
      return t('SPANISH');
    } else {
      return t('ENGLISH');
    }
  }

  return (
    <>
      <Button aria-controls='language-menu' aria-haspopup='true' onClick={openLanguageMenu}>
        <LanguageIcon />
        {getCurrentLanguage()}
        <ExpandMoreIcon fontSize='small' />
      </Button>
      <Menu
        id='language-menu'
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={closeLanguageMenu}
      >
        <MenuItem onClick={() => setLanguage('en')}>{t('ENGLISH')}</MenuItem>
        <MenuItem onClick={() => setLanguage('es')}>{t('SPANISH')}</MenuItem>
      </Menu>
    </>
  );
};

export const Layout = ({ children }: { children: any }) => {
  const classes = useStyles();
  const { t } = useTranslation('layout');
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down('xs'));
  const [cookies, setCookie] = useCookies(['cookies_accepted']);

  const acceptCookies = () => {
    setCookie('cookies_accepted', new Date().toISOString(), { path: '/', sameSite: true, maxAge: 99999999 });
  }

  React.useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 150) {
        acceptCookies();
        window.removeEventListener('scroll', handleScroll);
      }
    }
    if (!cookies['cookies_accepted']) {
      window.addEventListener('scroll', handleScroll);
    }
  }, []);



  return (
    <div className={classes.root}>
      <AppBar position='static' color='transparent' variant='outlined'>
        <Container className={classes.toolBarContainer}>
          <Toolbar>
            <Grid container alignItems='center'>
              <Grid item xs={12} sm={4} >
                <Grid container justify={isXs ? 'center' : 'flex-start'}>
                  <Grid item>
                    <Link component={RouterLink} to='/'>
                      <img className={classes.logo} src={logo} />
                    </Link>
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12} sm={8} >
                <Grid container justify={isXs ? 'center' : 'flex-end'}>
                  <Grid item>
                    <Button component={RouterLink} to='/deposit' color='inherit'>{t('NEW DEPOSIT')}</Button>
                  </Grid>
                  <Grid item>
                    <Button component={RouterLink} to='/account' color='inherit'>{t('MY ACCOUNT')}</Button>
                  </Grid>
                  <Grid item>
                    <LanguageButton />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Toolbar>
        </Container>
      </AppBar>
      {!cookies['cookies_accepted'] &&
        <Container>
          <Box padding={2}>
            <Alert severity="info">
              <Grid container alignContent='center' justify='center' alignItems='center' spacing={2}>
                <Grid item sm={10} xs={12}>
                  <Trans t={t} i18nKey='COOKIES'>
                    <Typography align='justify'>
                      Revoluex uses cookies to guarantee users the employment of its site features, offering a better purchasing experience and by continuing to browse the site you agree to our use of cookies.
                    </Typography>
                    <Link component={RouterLink} to='/cookies'>
                      <Typography align='justify'>
                        Check out our detailed cookies policy.
                      </Typography>
                    </Link>
                  </Trans>
                </Grid>
                <Grid item sm={2} xs={12}>
                  <Grid container justify='center'>
                    <Grid item>
                      <Button variant='contained' color='primary' onClick={acceptCookies}>{t('Accept')}</Button>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Alert>
          </Box>
        </Container>
      }
      <div className={classes.container}>
        {children}
      </div>
      <Paper className={classes.footerContainer} variant='outlined' square={true}>
        <Container className={classes.footerWrapper}>
          <Grid container spacing={2}>
            <Grid item md={6} xs={12}>
              <Grid container justify='center' alignContent='center' alignItems='center' spacing={1}>
                <Grid item xs={12}>
                  <Grid container justify='center'>
                    <Grid item>
                      <img src={revolugroupLogo} style={{ width: '180px' }} />
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item xs={12}>
                  <Trans t={t} i18nKey='REVOLUPAY_INFO'>
                    <Typography variant='body2' align='center' className={classes.footerText}>
                      RevoluEX is a division of RP PAYMENT SERVICES SLU wholly owned subsidiary of RevoluGROUP Canada Inc. a publicly traded company on the Toronto Stock Exchange - REVO. As part of the same group it uses RevoluPAY Payment Accounts for its fiat currency transactions
                    </Typography>
                  </Trans>
                </Grid>
                <Grid item xs={12}>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Grid container justify='flex-end'>
                        <Grid item>
                          <Link href='https://itunes.apple.com/us/app/revolupay/id1437640699?mt=8&ign-mpt=uo%3D4' rel='noopener' target='_blank'>
                            <img style={{ width: 150, maxWidth: '100%' }} src={appStoreLogo} />
                          </Link>
                        </Grid>
                      </Grid>
                    </Grid>
                    <Grid item xs={6}>
                      <Grid container justify='flex-start'>
                        <Grid item>
                          <Link href='https://play.google.com/store/apps/details?id=com.revolupayclient.vsla.revolupayconsumer' rel='noopener' target='_blank'>
                            <img style={{ width: 150, maxWidth: '100%' }} src={playstoreLogo} />
                          </Link>
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
            <Grid item direction='column' md={2} xs={4} container justify='flex-start' alignItems='center' alignContent='center'>
              <Grid item>
                <Typography variant='h6' className={classes.footerText} align='center'>{t('LEGAL')}</Typography>
              </Grid>
              <Grid item>
                <List dense>
                  <ListItem component={RouterLink} to='/about' className={classes.footerText}>
                    <ListItemText primary={t('About us')} />
                  </ListItem>
                  <ListItem component={RouterLink} to='/terms' className={classes.footerText}>
                    <ListItemText primary={t('User Agrement')} />
                  </ListItem>
                  <ListItem component={RouterLink} to='/privacy' className={classes.footerText}>
                    <ListItemText primary={t('Privacy Policy')} />
                  </ListItem>
                  <ListItem component={RouterLink} to='/cookies' className={classes.footerText}>
                    <ListItemText primary={t('Cookies Policy')} />
                  </ListItem>
                  <ListItem component={RouterLink} to='/credits' className={classes.footerText}>
                    <ListItemText primary={t('Credits')} />
                  </ListItem>
                </List>
              </Grid>
            </Grid>
            <Grid item direction='column' md={2} xs={4} container justify='flex-start' alignItems='center' alignContent='center'>
              <Grid item>
                <Typography variant='h6' className={classes.footerText} align='center'>{t('SERVICES')}</Typography>
              </Grid>
              <Grid item>
                <List dense>
                  <ListItem component='a' href='https://www.revolupay.es' rel='noopener' target='_blank' className={classes.footerText}>
                    <ListItemText primary={t('RevoluPAY')} />
                  </ListItem>
                  <ListItem component='a' href='https://www.revoluvip.club/' rel='noopener' target='_blank' className={classes.footerText}>
                    <ListItemText primary={t('RevoluVIP')} />
                  </ListItem>
                  <ListItem component='a' href='https://www.revolucharge.com' rel='noopener' target='_blank' className={classes.footerText}>
                    <ListItemText primary={t('RevoluCHARGE')} />
                  </ListItem>
                  <ListItem component='a' href='https://www.revolutility.com' rel='noopener' target='_blank' className={classes.footerText}>
                    <ListItemText primary={t('RevolUTILITY')} />
                  </ListItem>
                  <ListItem component='a' href='https://www.revoluegame.com' rel='noopener' target='_blank' className={classes.footerText}>
                    <ListItemText primary={t('RevoluEGAME')} />
                  </ListItem>
                  <ListItem component='a' href='https://www.revolufin.com/' rel='noopener' target='_blank' className={classes.footerText}>
                    <ListItemText primary={t('RevoluFIN')} />
                  </ListItem>
                </List>
              </Grid>
            </Grid>
            <Grid item direction='column' md={2} xs={4} container justify='flex-start' alignItems='center' alignContent='center'>
              <Grid item>
                <Typography variant='h6' className={classes.footerText} align='center'>{t('SUPPORT')}</Typography>
              </Grid>
              <Grid item>
                <List dense>
                  <ListItem component={RouterLink} to='/contact' className={classes.footerText}>
                    <ListItemText primary={t('Contact us')} />
                  </ListItem>
                </List>
              </Grid>
              <Grid item>
                <IconButton component='a' rel='noopener' target='_blank' href='https://www.facebook.com/pages/category/Finance/RevoluPay-928692330663160/' aria-label='facebook'>
                  <FacebookIcon fontSize={'small'} className={classes.footerText} />
                </IconButton>
                <IconButton component='a' rel='noopener' target='_blank' href='https://ve.linkedin.com/company/revolupay' aria-label='linkedin'>
                  <LinkedInIcon fontSize={'small'} className={classes.footerText} />
                </IconButton>
              </Grid>
            </Grid>
          </Grid>
        </Container>
      </Paper>
    </div>
  )
};

